import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Truck, Plus, Search, Edit2, Trash2, Phone, Mail,
  User, X, Loader2, CheckCircle, Clock, XCircle
} from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  available: { label: 'Available', color: '#16A34A', bg: '#DCFCE7', icon: CheckCircle },
  on_job: { label: 'On Job', color: '#D97706', bg: '#FEF3C7', icon: Clock },
  offline: { label: 'Offline', color: '#6B7280', bg: '#F3F4F6', icon: XCircle },
};

const VEHICLE_TYPES = ['Truck', 'Van', 'Ute', 'Tipper', 'Hook Lift', 'Skip Bin Truck', 'Other'];

const AdminDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '', mobile: '', email: '', vehicle_type: '',
    vehicle_rego: '', license_number: '', status: 'available', notes: '',
    active: true, password: ''
  });

  const token = localStorage.getItem('admin_token');

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await safeFetch(`${API_URL}/api/admin/drivers/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) setDrivers(result.data.drivers || []);
    } catch (err) {
      console.error('Failed to fetch drivers:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  const resetForm = () => {
    setFormData({ name: '', mobile: '', email: '', vehicle_type: '', vehicle_rego: '', license_number: '', status: 'available', notes: '', active: true, password: '' });
    setEditingDriver(null);
    setShowForm(false);
  };

  const openEdit = (driver) => {
    setFormData({
      name: driver.name || '', mobile: driver.mobile || '', email: driver.email || '',
      vehicle_type: driver.vehicle_type || '', vehicle_rego: driver.vehicle_rego || '',
      license_number: driver.license_number || '', status: driver.status || 'available',
      notes: driver.notes || '', active: driver.active !== false, password: ''
    });
    setEditingDriver(driver);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const url = editingDriver
        ? `${API_URL}/api/admin/drivers/${editingDriver.id}`
        : `${API_URL}/api/admin/drivers/`;
      const result = await safeFetch(url, {
        method: editingDriver ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (result.ok) {
        resetForm();
        fetchDrivers();
      }
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete driver "${name}"? They will be unassigned from all bookings.`)) return;
    try {
      await safeFetch(`${API_URL}/api/admin/drivers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDrivers();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const filtered = drivers.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.mobile?.includes(search) ||
    d.vehicle_rego?.toLowerCase().includes(search.toLowerCase())
  );

  const statusCounts = {
    available: drivers.filter(d => d.status === 'available').length,
    on_job: drivers.filter(d => d.status === 'on_job').length,
    offline: drivers.filter(d => d.status === 'offline').length,
  };

  return (
    <div className="space-y-6" data-testid="admin-drivers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Drivers</h1>
          <p className="text-[#64748B] text-sm mt-1">Manage your fleet drivers and assignments</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-[#0D9488] hover:bg-[#0F766E]"
          data-testid="add-driver-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Driver
        </Button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <Card key={key} className="border-0 shadow-sm" data-testid={`driver-status-${key}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.bg }}>
                  <Icon className="w-5 h-5" style={{ color: config.color }} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111111]">{statusCounts[key]}</p>
                  <p className="text-xs text-[#64748B]">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input
          placeholder="Search by name, mobile, or rego..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="driver-search"
        />
      </div>

      {/* Driver Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && resetForm()}>
          <Card className="w-full max-w-lg border-0 shadow-xl" data-testid="driver-form-modal">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{editingDriver ? 'Edit Driver' : 'Add New Driver'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm} data-testid="close-driver-form">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Name *</label>
                  <Input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} placeholder="Driver name" data-testid="driver-name-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Mobile</label>
                  <Input value={formData.mobile} onChange={e => setFormData(p => ({...p, mobile: e.target.value}))} placeholder="0400 000 000" data-testid="driver-mobile-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#64748B] mb-1 block">Email</label>
                <Input value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))} placeholder="driver@email.com" data-testid="driver-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Vehicle Type</label>
                  <select
                    value={formData.vehicle_type}
                    onChange={e => setFormData(p => ({...p, vehicle_type: e.target.value}))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="driver-vehicle-type"
                  >
                    <option value="">Select type</option>
                    {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Vehicle Rego</label>
                  <Input value={formData.vehicle_rego} onChange={e => setFormData(p => ({...p, vehicle_rego: e.target.value}))} placeholder="ABC 123" data-testid="driver-rego-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">License Number</label>
                  <Input value={formData.license_number} onChange={e => setFormData(p => ({...p, license_number: e.target.value}))} placeholder="License #" data-testid="driver-license-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData(p => ({...p, status: e.target.value}))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="driver-status-select"
                  >
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#64748B] mb-1 block">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(p => ({...p, notes: e.target.value}))}
                  placeholder="Any additional notes..."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px] resize-none"
                  data-testid="driver-notes-input"
                />
              </div>

              {/* Transa GO Access */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-[#64748B] uppercase mb-3">Transa GO Access</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-[#64748B] mb-1 block">
                      {editingDriver ? 'New Password (leave blank to keep)' : 'Portal Password *'}
                    </label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={e => setFormData(p => ({...p, password: e.target.value}))}
                      placeholder={editingDriver ? 'Leave blank to keep' : 'Set password'}
                      data-testid="driver-password-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#64748B] mb-1 block">Account Status</label>
                    <div
                      onClick={() => setFormData(p => ({...p, active: !p.active}))}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                        formData.active
                          ? 'border-green-300 bg-green-50 text-green-700'
                          : 'border-red-300 bg-red-50 text-red-700'
                      }`}
                      data-testid="driver-active-toggle"
                    >
                      <div className={`w-8 h-5 rounded-full relative transition-colors ${formData.active ? 'bg-green-500' : 'bg-red-400'}`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${formData.active ? 'left-3.5' : 'left-0.5'}`} />
                      </div>
                      <span className="text-sm font-medium">{formData.active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={resetForm} className="flex-1" data-testid="cancel-driver-btn">Cancel</Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || !formData.name.trim()}
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E]"
                  data-testid="save-driver-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingDriver ? 'Update' : 'Create'} Driver
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Drivers List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0D9488]" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <Truck className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
            <p className="text-[#64748B]">{search ? 'No drivers match your search' : 'No drivers yet. Add your first driver to get started.'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(driver => {
            const statusConf = STATUS_CONFIG[driver.status] || STATUS_CONFIG.offline;
            const StatusIcon = statusConf.icon;
            return (
              <Card key={driver.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`driver-card-${driver.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-[#F0FDFA] flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-[#0D9488]" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#111111] truncate">{driver.name}</h3>
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0"
                          style={{ backgroundColor: statusConf.bg, color: statusConf.color }}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {statusConf.label}
                        </span>
                        {driver.active === false && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 flex-shrink-0">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-[#64748B]">
                        {driver.mobile && (
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{driver.mobile}</span>
                        )}
                        {driver.email && (
                          <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{driver.email}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#94A3B8] mt-1">
                        {driver.vehicle_type && (
                          <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{driver.vehicle_type}</span>
                        )}
                        {driver.vehicle_rego && (
                          <span className="bg-[#F1F5F9] px-1.5 py-0.5 rounded font-mono">{driver.vehicle_rego}</span>
                        )}
                        {driver.active_booking_count > 0 && (
                          <span className="text-[#D97706]">{driver.active_booking_count} active job{driver.active_booking_count > 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(driver)} data-testid={`edit-driver-${driver.id}`}>
                        <Edit2 className="w-4 h-4 text-[#64748B]" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(driver.id, driver.name)} data-testid={`delete-driver-${driver.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDrivers;
